import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { AuthContext } from "./AuthContext.jsx";
import log from 'loglevel';

log.setLevel(process.env.NODE_ENV === 'production' ? 'error' : 'debug');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const verifyToken = useCallback(async (token) => {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      log.debug("Verify response:", response.data);
      return response.data;
    } catch (err) {
      log.error("Token validation failed:", err.response?.data || err.message);
      throw err;
    }
  }, []);

  useEffect(() => {
    // ✅ CRITICAL FIX: Determine context ONCE on mount, don't re-check on every render
    const isKioskRoute = window.location.pathname.startsWith('/kiosk');
    const tokenKey = isKioskRoute ? "kioskAuthToken" : "adminAuthToken";
    const userKey = isKioskRoute ? "kioskUser" : "adminUser";

    log.debug("Initializing auth context", { 
      isKioskRoute, 
      tokenKey, 
      userKey,
      currentPath: window.location.pathname 
    });

    const token = localStorage.getItem(tokenKey);
    const storedUser = localStorage.getItem(userKey);
    
    if (token && storedUser) {
      log.debug("Restoring user from localStorage:", storedUser);
      log.debug("Token:", token);
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        
        verifyToken(token)
          .then(({ user: verifiedUser }) => {
            log.debug("Verified user:", verifiedUser);
            
            // ✅ FIXED: More lenient validation - only check role compatibility
            if (isKioskRoute) {
              // Kiosk routes: only cashier, customer_service, manager allowed
              if (!['cashier', 'customer_service', 'manager'].includes(verifiedUser.role)) {
                log.warn("Invalid role for kiosk route:", verifiedUser.role);
                throw new Error("Access denied for this role");
              }
            } else {
              // Admin routes: admin and accountant allowed
              if (!['admin', 'accountant'].includes(verifiedUser.role)) {
                log.warn("Invalid role for admin route:", verifiedUser.role);
                throw new Error("Admin access required");
              }
            }
            
            setUser(verifiedUser);
            localStorage.setItem(userKey, JSON.stringify(verifiedUser));
            log.debug(`User verified for ${isKioskRoute ? 'kiosk' : 'admin'} route:`, verifiedUser.role);
          })
          .catch((err) => {
            log.error("Token validation failed:", err.response?.data || err.message);
            setError(err.response?.data?.message || "Failed to verify session");
            localStorage.removeItem(tokenKey);
            localStorage.removeItem(userKey);
            delete axios.defaults.headers.common["Authorization"];
            setUser(null);
          })
          .finally(() => setLoading(false));
      } catch (parseError) {
        log.error("Failed to parse stored user:", parseError);
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(userKey);
        setUser(null);
        setLoading(false);
      }
    } else {
      log.debug("No token or user found in localStorage");
      setLoading(false);
    }
  }, [verifyToken]);

  const login = async (identifier, password, isKiosk = false) => {
    log.debug("Sending login request with:", { identifier, isKiosk });
    setError(null);
    setLoading(true);
    
    const endpoint = isKiosk ? "/api/auth/kiosk/login" : "/api/auth/login";
    const payload = isKiosk ? { staffId: identifier, pin: password.toString() } : { username: identifier, password };
    
    // ✅ CRITICAL FIX: Use separate storage keys for admin vs kiosk
    const tokenKey = isKiosk ? "kioskAuthToken" : "adminAuthToken";
    const userKey = isKiosk ? "kioskUser" : "adminUser";
    
    try {
      const res = await axios.post(`${import.meta.env.VITE_API_URL}${endpoint}`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      log.debug("Login response:", res.data);
      const { token, user } = res.data;
      
      if (!token || !user) {
        throw new Error("Invalid response: Missing token or user");
      }
      
      // ✅ CRITICAL: Validate role matches login context
      if (isKiosk) {
        if (!['cashier', 'customer_service', 'manager'].includes(user.role)) {
          throw new Error("Invalid role for kiosk login");
        }
      } else {
        if (!['admin', 'accountant'].includes(user.role)) {
          throw new Error("Admin credentials required");
        }
      }
      
      // ✅ Clear the OTHER context's storage to prevent cross-contamination
      const otherTokenKey = isKiosk ? "adminAuthToken" : "kioskAuthToken";
      const otherUserKey = isKiosk ? "adminUser" : "kioskUser";
      localStorage.removeItem(otherTokenKey);
      localStorage.removeItem(otherUserKey);
      
      localStorage.setItem(tokenKey, token);
      localStorage.setItem(userKey, JSON.stringify(user));
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setUser(user);
      log.debug("Login successful, user set:", user);
      
      return res.data;
    } catch (err) {
      log.error("Login error:", err.response?.data || err.message);
      throw new Error(err.response?.data?.message || err.message || "Failed to log in");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    log.debug("Logging out user");
    
    // ✅ CRITICAL FIX: Clear BOTH storage contexts on logout
    localStorage.removeItem("adminAuthToken");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("kioskAuthToken");
    localStorage.removeItem("kioskUser");
    
    // Also clear legacy keys if they exist
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
    setError(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, error }}>
      {children}
    </AuthContext.Provider>
  );
};